import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import BatterySaverOutlinedIcon from '@mui/icons-material/BatterySaverOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CandlestickChartOutlinedIcon from '@mui/icons-material/CandlestickChartOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import Tooltip from '@mui/material/Tooltip';

export default function DashboardGeneral() {
  const state = useContext(MainContext).state;
  const r2 = state.R2.split(',');
  const r4 = state.R4.split(',');
  const r5 = state.R5.split(',');

  // --- VALUES ---
  const waterDetected = r2[14] === '1' ? 'Yes' : 'No';

  let psav = '0°C';
  if (r2[15].includes('1')) psav = '-0.5°C';
  if (r2[15].includes('2')) psav = '+0.5°C';

  const timeout = new Date(r2[19] * 1000).toISOString().slice(11, 19);

  // TP1
  let tp1 = 'None';
  if (r5[2] !== '0') {
    const idx = r5[4];
    const opts = ['SV-2T', 'SV-3T', 'SV-4T', 'SV-4T', 'SV-RT', 'SV-MT', 'SV-T43'];
    tp1 = opts[idx] || 'None';
  }

  // TP2
  let tp2 = 'None';
  if (r5[1] !== '0') {
    const idx = r5[3];
    const opts = ['SV-2T', 'SV-3T', 'SV-4T', 'SV-4T', 'SV-RT', 'SV-MT', 'SV-T43'];
    tp2 = opts[idx] || 'None';
  }

  // SER counters
  const ser1 = r4[2] === '0' || r4[2] === '...' ? '0' : r4[2];
  const ser2 = r4[3] === '0' || r4[3] === '...' ? '0' : r4[3];
  const ser3 = r4[4] === '0' || r4[4] === '...' ? '0' : r4[4];

  const rows = [
    { id: 0, label: 'Water detected', icon: <WaterDropOutlinedIcon />, value: waterDetected },
    { id: 1, label: 'PSAV Offset', icon: <BatterySaverOutlinedIcon />, value: psav },
    { id: 2, label: 'T.OUT counter', icon: <TimerOutlinedIcon />, value: timeout },
    { id: 3, label: 'Touch Pad 1', icon: <CandlestickChartOutlinedIcon />, value: tp1 },
    { id: 4, label: 'Touch Pad 2', icon: <CandlestickChartOutlinedIcon />, value: tp2 },
    { id: 5, label: '1.SER counter', icon: <CalculateOutlinedIcon />, value: ser1 },
    { id: 6, label: '2.SER counter', icon: <CalculateOutlinedIcon />, value: ser2 },
    { id: 7, label: '3.SER counter', icon: <CalculateOutlinedIcon />, value: ser3 },
  ];

  const tooltips = {
    'Water detected': 'Water detected',
    'T.OUT counter': 'Timeout Counter',
    'PSAV Offset': 'Powersave Offset',
    'Touch Pad 1': 'Touch Pad 1',
    'Touch Pad 2': 'Touch Pad 2',
    '1.SER counter': 'Service Timer 1 counter',
    '2.SER counter': 'Service Timer 2 counter',
    '3.SER counter': 'Service Timer 3 counter',
  };

  return (
    <React.Fragment>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        General
      </Typography>

      <Grid container spacing={1.5}>
        {rows.map((item) => {
          const isWaterDetected =
            item.label === 'Water detected' && item.value === 'Yes';

          const tooltipText = tooltips[item.label];

          const card = (
            <Paper
              sx={{
                p: 1.2,
                minHeight: {
                  xs: 75,
                  sm: 'auto',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid',
                borderColor: 'rgba(255,255,255,0.1)',
                transition: '0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              {/* Icon bubble */}
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isWaterDetected
                    ? 'success.main'
                    : 'rgba(255,255,255,0.05)',
                  color: isWaterDetected ? '#000' : 'rgba(50,116,162,1)',
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>

              {/* Label + value */}
              <Box sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.3, 
                  mt: 0.4
                }}>
                <Typography
                  variant="subtitle2"
                  color="white"
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  sx={{
                    color: isWaterDetected
                      ? 'success.main'
                      : 'rgba(255,255,255,0.6)',
                    fontSize: 13,
                  }}
                  noWrap
                >
                  {item.value}
                </Typography>
              </Box>
            </Paper>
          );

          return (
            <Grid item xs={6} key={item.id}>
              {tooltipText ? (
                <Tooltip title={tooltipText} arrow placement="top">
                  <Box>{card}</Box>
                </Tooltip>
              ) : (
                card
              )}
            </Grid>
          );
        })}
      </Grid>
    </React.Fragment>
  );
}
